/**
 * BrandBlobs — fixed-position, behind-everything ambient gradient blobs.
 *
 * Three large radial gradients (peach + orange tint + sand) drift slowly
 * across the viewport, giving the workspace shell an organic "Ando" warm-
 * paper background with a soft orange brand hint. Pointer-events: none
 * + z-[-1] so they never block clicks or sit above content.
 *
 * Mockup ref: `.bg` + `.bg::before` + `.bg::after` in
 * `tasks/mockups/ando_v18.html` — extended here with a third orange-tinted
 * blob centered behind the workspace pane so the brand colour reads
 * through the translucent surfaces.
 */

import type * as React from "react";

export const BrandBlobs: React.FC = () => (
  // z-0 (not z-[-1]) so the blobs sit ABOVE the body's solid cream
  // background. The workspace panes (z-auto via position:relative) layer
  // on top with their cream/40 + cream/30 translucency and the brand
  // colour reads through.
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-0 overflow-hidden motion-reduce:hidden"
  >
    {/* Top-left warm peach — barely there, just hints at warmth. */}
    <div
      className="absolute h-[70vw] w-[70vw] animate-blob-drift-1 rounded-full opacity-30 blur-[100px]"
      style={{
        top: "-25vw",
        left: "-15vw",
        background:
          "radial-gradient(circle, #FFC894 0%, transparent 65%)",
      }}
    />
    {/* Centre-right brand orange — a whisper of the Ando hue. */}
    <div
      className="absolute h-[60vw] w-[60vw] animate-blob-drift-2 rounded-full opacity-20 blur-[110px]"
      style={{
        top: "5vh",
        right: "-15vw",
        background:
          "radial-gradient(circle, rgba(255,122,26,0.70) 0%, transparent 65%)",
      }}
    />
    {/* Bottom-left warm sand — neutral anchor, almost transparent. */}
    <div
      className="absolute h-[65vw] w-[65vw] animate-blob-drift-1 rounded-full opacity-25 blur-[110px]"
      style={{
        bottom: "-25vw",
        left: "15vw",
        background:
          "radial-gradient(circle, #F3E5C9 0%, transparent 65%)",
        animationDelay: "-25s",
      }}
    />
  </div>
);
BrandBlobs.displayName = "BrandBlobs";
